// src/components/BoardPack/pages/kpi_chart_on_street.jsx
import React, { useState, useEffect, useRef } from "react";
import { apiCall } from "@/api/apiCall";
import LoadingComponent from "@/components/common/LoadingComponent";
import ErrorState from "@/components/common/ErrorStateComponent";
import NewChartKPI from "../components/charts/NewChartKPI";
import { useQueue } from "@/context/QueueContext";
import { processParams } from "../utils/paramsHelper";
import { useNavigate } from "react-router-dom";

const NewChartKPIPage = ({
  year = "",
  month = "",
  monthNumber = "",
  contract = "",
  page = null,
  isRefresh = false 
}) => {
  const { enqueue } = useQueue();
const navigate = useNavigate();
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const hasFetchedRef             = useRef(false);

  // ── Transform API response → chartData rows ─────────────────────────────
  const transformApiData = (apiData) => {
    const contractNames = Object.keys(apiData?.data ?? {});
    if (!contractNames.length) return [];

    const contractData = apiData.data[contractNames[0]];

    // helper: safely parse "12,277" or "25%" → number
    const parseNum = (val) => {
      if (val == null) return 0;
      return parseFloat(String(val).replace(/[,%]/g, "")) || 0;
    };

    const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    // derive years from revenue key — skip non-year keys like "isPercent"
    const revenueObj = contractData?.Monthly_Avg_Daily_Parking_Revenue ?? {};
    const years = Object.keys(revenueObj).filter((k) => /^\d{4}$/.test(k));

    const transformedData = [];

    years.forEach((yr) => {
      MONTHS.forEach((mon) => {
        const revenueRaw = revenueObj?.[yr]?.[mon];
        if (!revenueRaw) return; // skip months with no data

        transformedData.push({
          month:       `${mon}-${yr}`,
          year:        yr,
          revenue:     parseNum(revenueRaw),
          parkingBays: parseNum(contractData?.Total_Parking_Bays?.[yr]?.[mon]),
          transaction: parseNum(contractData?.Monthly_Avg_Daily_Transaction?.[yr]?.[mon]),
          occupancy:   parseNum(contractData?.Parking_Occupancy?.[yr]?.[mon]),
          isRamadan:
            contractData?.Ramadan?.[yr]?.[mon] === true ||
            contractData?.Ramadan?.[yr]?.[mon] === "true",
        });
      });
    });

    return transformedData;
  };

  // ── Fetch guard (same pattern as KpiChart12) ────────────────────────────
  useEffect(() => {
    if (!year || !month || !contract) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchData();
  }, [year, month, contract]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const apiParams = processParams(page?.params || {}, { year, month, contract });
      const response = await enqueue(() => apiCall("GET", "/board-pack/page-twelve", apiParams,navigate,false,false,isRefresh));

      if (response?.success) {
        const transformed = transformApiData(response);
        setChartData(transformed);
      } else {
        setError(response?.message || "Failed to load data. Please try again later.");
      }
    } catch (err) {
      setError(err?.message || "Failed to load data. Please try again later.");
      console.error("Error fetching on-street data:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingComponent />;
  if (error)   return <ErrorState error={error} onRetry={fetchData} />;

  return (
    <NewChartKPI
      chartData={chartData}
      title={page?.title}
      subtitle={page?.subtitle}
      chartTitle={page?.in_chart_content_line_1}
      colors={page?.colors}
      labels={page?.labels}
      showDataLabels={page?.showDataLabels ? true : false}
      showRamadanRow={page?.showRamadan || true}
      ramadanborderColor={page?.ramadanBorderColor || "#FFA500"}
      yAxisFormatters={{
        left:  (value) => value.toLocaleString(),
        right: (value) => `${value}%`,
      }}
    />
  );
};

export default NewChartKPIPage;